import { Component, OnInit } from '@angular/core';
import { LocaleService } from 'src/app/services/locale.service';
import { Title } from '@angular/platform-browser';
import { AppointmentService } from 'src/app/services/appointment.service';
import { Appointment } from 'src/app/models/appointment.model';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { ToastService } from 'src/app/services/toast.service';
import moment from 'moment';
import { ProviderCustomizeService } from 'src/app/services/provider-customize.service';
import { MessageNavigationService } from 'src/app/services/message-navigation.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { Patient } from 'src/app/models/patient.model';

@Component({
  selector: 'app-appointment-cancel',
  templateUrl: './appointment-cancel.component.html',
  styleUrls: ['./appointment-cancel.component.css'],
  standalone: false
})
export class AppointmentCancelComponent implements OnInit {
  appointment!: Appointment;
  disableCancelButton = false;
  isLoading = true;

  constructor(
    private localeService: LocaleService,
    private title: Title,
    private appointmentService: AppointmentService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private providerCustomizeService: ProviderCustomizeService,
    private messageNavigationService: MessageNavigationService,
    private sharedDataService: SharedDataService,
  ) {
    this.title.setTitle(this.localeService.getString('appointment_cancel_title'));
  }

  async ngOnInit(): Promise<void> {
    this.providerCustomizeService.beginProviderResolution();
    if (!(await this.getAppointment())) {
      return;
    }

    this.providerCustomizeService.resolveProvider(this.appointment?.provider);

    if(this.appointment?.cancelled) {
      this.disableCancelButton = true;
      this.openDialog("appointment_cancel_already_cancelled_dialog");
      this.isLoading = false;
      return;
    }

    this.canCancelAppointment();
    this.isLoading = false;
  }

  async getAppointment(): Promise<boolean> {
    try {
      this.appointment = await this.appointmentService.getOne();
      const user = { ...this.appointment.patient, provider: this.appointment.provider } as Patient;
      this.sharedDataService.setCurrentUser(user);
      return true;
    } catch (error) {
      this.providerCustomizeService.clear();
      this.messageNavigationService.goToMessage("appointment_cannot_cancel");
      this.isLoading = false;
      return false;
    }
  }

  openDialog(message: string, fn = () => undefined): void {
    const data = {
      message: message,
      title: "default_warning_message",
      actions: [{
        function: fn,
        message: "default_accept"
      }]
    }

    this.dialog.open(DialogComponent, {
      width: "400px",
      panelClass: 'bg-dark',
      disableClose: true,
      data,
    });
  }

  canCancelAppointment(): boolean {
    const duration = moment.duration(moment(this.appointment.date).diff(moment()));
    const minutes = duration.asMinutes();

    if(minutes < 10) { // 10 minutes or less left to start appointment
      this.disableCancelButton = true;
      this.openDialog("appointment_cancel_about_to_start");
      return false;
    }

    return true;
  }

  async submit(): Promise<void> {
    if(!this.canCancelAppointment()) return;

    try {
      await this.appointmentService.cancelWithToken();
      this.openDialog("appointment_cancel_cancelled_dialog", () => { this.disableCancelButton = true });
    } catch (error) {
      this.toastService.openErrorToast(error?.error?.message);
    }
  }
}
